import React, { useState, useEffect } from 'react';
import { promptTemplates, stylePresets } from '../data/templates';
import { multiModelTemplates, modelCapabilities } from '../data/multiModelTemplates';
import { PromptTemplate, GeneratedPrompt, PromptCategory, ModelPlatform } from '../types/prompt';
import { Sparkles, Copy, Save, History, Info, Shuffle, Download, DollarSign, Cloud, CloudOff, Bot } from 'lucide-react';
import PromptHistory from './PromptHistory';
import UpgradeModal from './UpgradeModal';
import useLocalStorage from '../hooks/useLocalStorage';
import AIAssistant from './AIAssistant';
import { useAuth } from '../contexts/AuthContext';
import { promptsService, historyService } from '../services/firebase/firestore';
import { PromptDocument } from '../services/firebase/firestore';
import toast from 'react-hot-toast';
import { useApiKeys } from '../contexts/ApiKeyContext';
import { enhancePrompt } from '../services/ai/promptEnhancer';
import { enhancePromptPro } from '../services/ai/promptEnhancerPro';
import { subscriptionService } from '../services/subscription';

const PromptBuilderEnhanced: React.FC = () => {
  const { user, profile, isAuthenticated, trackUsage } = useAuth();
  const { apiKeys } = useApiKeys();
  const [selectedTemplate, setSelectedTemplate] = useState<PromptTemplate | null>(null);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [promptTitle, setPromptTitle] = useState('');
  const [category, setCategory] = useState<PromptCategory | 'all'>('image');
  const [selectedPlatform, setSelectedPlatform] = useState<ModelPlatform | 'all'>('all');
  const [showAdvancedTemplates, setShowAdvancedTemplates] = useState(false);
  const [promptHistory, setPromptHistory] = useLocalStorage<GeneratedPrompt[]>('promptHistory', []);
  const [cloudPrompts, setCloudPrompts] = useState<PromptDocument[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [savingToCloud, setSavingToCloud] = useState(false);
  const [loadingCloudPrompts, setLoadingCloudPrompts] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);

  const handleTemplateSelect = (template: PromptTemplate) => {
    setSelectedTemplate(template);
    const initialVars: Record<string, string> = {};
    template.variables.forEach(v => {
      initialVars[v] = template.parameters?.[v] || '';
    });
    setVariables(initialVars);
    generatePrompt(template, initialVars);
    
    // Auto-scroll to customize section with smooth animation
    setTimeout(() => {
      const customizeSection = document.querySelector('.customize-section');
      if (customizeSection) {
        customizeSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  const generatePrompt = (template: PromptTemplate, vars: Record<string, string>) => {
    let prompt = template.template;
    Object.entries(vars).forEach(([key, value]) => {
      prompt = prompt.replace(`{${key}}`, value || `[${key}]`);
    });
    setGeneratedPrompt(prompt);
  };

  const handleVariableChange = (key: string, value: string) => {
    const newVars = { ...variables, [key]: value };
    setVariables(newVars);
    if (selectedTemplate) {
      generatePrompt(selectedTemplate, newVars);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedPrompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const enhanceWithAI = async () => {
    if (!generatedPrompt || isEnhancing) return;

    setIsEnhancing(true);
    try {
      // Use Pro-aware enhancer
      const enhanced = await enhancePromptPro(generatedPrompt, apiKeys);
      if (enhanced) {
        setGeneratedPrompt(enhanced);
        toast.success('Prompt enhanced with AI!');
      } else {
        toast.error('Could not enhance prompt. Please check your API keys.');
      }
    } catch (error) {
      console.error('Error enhancing prompt:', error);
      toast.error('Failed to enhance prompt');
    } finally {
      setIsEnhancing(false);
    }
  };

  // Load user's cloud prompts
  useEffect(() => {
    if (isAuthenticated && user) {
      loadCloudPrompts();
    }
  }, [isAuthenticated, user]);

  const loadCloudPrompts = async () => {
    if (!user) return;
    
    setLoadingCloudPrompts(true);
    try {
      const result = await promptsService.getUserPrompts(user.uid, 20);
      setCloudPrompts(result.data);
    } catch (error) {
      console.error('Error loading cloud prompts:', error);
    } finally {
      setLoadingCloudPrompts(false);
    }
  };

  const saveToHistory = async () => {
    if (!generatedPrompt || !selectedTemplate) return;
    
    // Check if user can save prompts to cloud
    if (!subscriptionService.canSavePrompts(profile)) {
      // Show upgrade modal for free users
      setShowUpgradeModal(true);
      toast.error('Upgrade to Pro to save prompts to cloud');
      return;
    }
    
    // Save to local storage (temporary history for session)
    const newPrompt: GeneratedPrompt = {
      id: Date.now().toString(),
      prompt: generatedPrompt,
      platform: selectedTemplate.platform,
      timestamp: new Date(),
      isFavorite: false,
      category: selectedTemplate.category
    };
    
    setPromptHistory([newPrompt, ...promptHistory].slice(0, 50));
    
    // Save to Firestore if authenticated and has Pro subscription
    if (isAuthenticated && user && subscriptionService.canSavePrompts(profile)) {
      setSavingToCloud(true);
      
      const savePromise = (async () => {
        const promptData = {
          userId: user.uid,
          title: promptTitle || `${selectedTemplate.name} - ${new Date().toLocaleDateString()}`,
          content: generatedPrompt,
          platform: selectedTemplate.platform,
          category: selectedTemplate.category,
          tags: selectedTemplate.tags || [],
          parameters: variables,
          isPublic: false,
          isFavorite: false
        };
        
        const promptId = await promptsService.create(promptData);
        
        // Track usage
        await trackUsage('promptsSaved');
        
        // Add to history
        await historyService.add({
          userId: user.uid,
          action: 'saved',
          content: generatedPrompt,
          platform: selectedTemplate.platform
        });
        
        // Reload cloud prompts
        await loadCloudPrompts();
        
        setSaved(true);
        return promptId;
      })();
      
      toast.promise(
        savePromise,
        {
          loading: 'Saving prompt to cloud...',
          success: '✅ Prompt saved successfully! Check your dashboard.',
          error: (err) => `Failed to save: ${err.message || err}`
        }
      );
      
      try {
        await savePromise;
      } catch (error) {
        console.error('Error saving to cloud:', error);
      } finally {
        setSavingToCloud(false);
      }
    } else {
      setSaved(true);
      toast.success('✅ Prompt saved to local history!');
    }
    
    setTimeout(() => setSaved(false), 2000);
  };

  const toggleFavorite = (id: string) => {
    setPromptHistory(promptHistory.map(p => 
      p.id === id ? { ...p, isFavorite: !p.isFavorite } : p
    ));
  };

  const deletePrompt = (id: string) => {
    setPromptHistory(promptHistory.filter(p => p.id !== id));
  };

  const copyPromptFromHistory = (prompt: string) => {
    navigator.clipboard.writeText(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const randomizeVariable = (variable: string) => {
    const presets = stylePresets[variable as keyof typeof stylePresets];
    if (presets && presets.length > 0) {
      const randomValue = presets[Math.floor(Math.random() * presets.length)];
      handleVariableChange(variable, randomValue);
    }
  };

  const downloadPrompt = () => {
    const element = document.createElement("a");
    const file = new Blob([generatedPrompt], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `prompt_${selectedTemplate?.platform}_${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleAIPromptUpdate = (newPrompt: string) => {
    setGeneratedPrompt(newPrompt);
    if (selectedTemplate) {
      // Parse the prompt back into variables if possible
      const vars = { ...variables };
      selectedTemplate.variables.forEach(v => {
        const regex = new RegExp(`\\[${v}\\]`, 'g');
        if (!regex.test(newPrompt)) {
          // If the variable has been filled in the AI prompt, extract it
          // This is a simplified approach - might need more sophisticated parsing
        }
      });
    }
  };

  const allTemplates = showAdvancedTemplates 
    ? [...promptTemplates, ...multiModelTemplates]
    : promptTemplates;
  
  const filteredTemplates = allTemplates.filter(t => {
    const categoryMatch = category === 'all' || t.category === category;
    const platformMatch = selectedPlatform === 'all' || t.platform === selectedPlatform;
    return categoryMatch && platformMatch;
  });

  const calculateCost = (template: PromptTemplate): string => {
    if (template.costPerUse) {
      return `$${template.costPerUse.toFixed(4)}`;
    }
    const capabilities = (modelCapabilities as any)[template.platform];
    if (capabilities) {
      if ('costPerToken' in capabilities) {
        return `$${(capabilities.costPerToken * 1000).toFixed(4)}/1K tokens`;
      }
      if ('costPerImage' in capabilities) {
        return `$${capabilities.costPerImage}/image`;
      }
      if ('costPerVideo' in capabilities) {
        return `$${capabilities.costPerVideo}/video`;
      }
    }
    return 'Free';
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <div className="flex justify-end mb-4 fade-in">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-lg shadow-md hover:shadow-lg transition-all hover-scale group dark:text-white"
        >
          <History className="w-4 h-4 group-hover:text-blue-500 transition-colors" />
          <span>History ({promptHistory.length})</span>
        </button>
      </div>
      
      {showHistory ? (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 mb-6 fade-in">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Prompt History
            </h2>
            <button
              onClick={() => setShowHistory(false)}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl font-bold transition-colors hover:rotate-90 transition-transform"
            >
              ×
            </button>
          </div>
          <PromptHistory
            prompts={promptHistory}
            onToggleFavorite={toggleFavorite}
            onDelete={deletePrompt}
            onCopy={copyPromptFromHistory}
          />
        </div>
      ) : null}
      
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 fade-in">
        <div className="mb-6">
          {/* Category Selection */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setCategory('all')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all hover-scale text-sm sm:text-base ${
                category === 'all'
                  ? 'bg-gradient-to-r from-sky-500 to-cyan-500 text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
            >
              🌐 All
            </button>
            <button
              onClick={() => setCategory('image')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all hover-scale text-sm sm:text-base ${
                category === 'image'
                  ? 'bg-gradient-to-r from-sky-500 to-cyan-500 text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
            >
              🎨 Image
            </button>
            <button
              onClick={() => setCategory('video')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all hover-scale text-sm sm:text-base ${
                category === 'video'
                  ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
            >
              🎬 Video
            </button>
            <button
              onClick={() => setCategory('text')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all hover-scale text-sm sm:text-base ${
                category === 'text'
                  ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
            >
              📝 Text
            </button>
            <button
              onClick={() => setCategory('code')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all hover-scale text-sm sm:text-base ${
                category === 'code'
                  ? 'bg-gradient-to-r from-sky-600 to-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
            >
              💻 Code
            </button>
            <button
              onClick={() => setCategory('analysis')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all hover-scale text-sm sm:text-base ${
                category === 'analysis'
                  ? 'bg-gradient-to-r from-blue-500 to-sky-500 text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
            >
              📊 Analysis
            </button>
            <button
              onClick={() => setCategory('creative')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all hover-scale text-sm sm:text-base ${
                category === 'creative'
                  ? 'bg-gradient-to-r from-cyan-600 to-sky-600 text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
            >
              ✨ Creative
            </button>
          </div>

          {/* Platform Filter */}
          <div className="flex items-center gap-4 mb-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Platform:</label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value as ModelPlatform | 'all')}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Platforms</option>
              <optgroup label="Image Generation">
                <option value="midjourney">Midjourney</option>
                <option value="google-imagefx">Google ImageFX</option>
                <option value="dalle3">DALL-E 3</option>
                <option value="stable-diffusion-xl">Stable Diffusion XL</option>
              </optgroup>
              <optgroup label="Video Generation">
                <option value="midjourney-video">Midjourney Video</option>
                <option value="kling-ai">Kling AI</option>
                <option value="veo">Google Veo</option>
              </optgroup>
              <optgroup label="AI Models">
                <option value="claude">Claude</option>
                <option value="gpt4">GPT-4</option>
                <option value="gpt4-vision">GPT-4 Vision</option>
                <option value="gemini">Gemini</option>
                <option value="llama">LLaMA</option>
                <option value="mistral">Mistral</option>
              </optgroup>
              <option value="universal">Universal</option>
            </select>
            
            <div className="flex items-center gap-2 ml-auto">
              <input
                type="checkbox"
                id="advanced"
                checked={showAdvancedTemplates}
                onChange={(e) => setShowAdvancedTemplates(e.target.checked)}
                className="rounded text-blue-600"
              />
              <label htmlFor="advanced" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                Show Advanced Templates
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {filteredTemplates.map(template => (
              <div
                key={template.id}
                onClick={() => handleTemplateSelect(template)}
                className={`p-3 sm:p-4 border-2 rounded-xl cursor-pointer transition-all hover-scale group ${
                  selectedTemplate?.id === template.id
                    ? 'border-cyan-500 bg-gradient-to-br from-sky-50 via-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:via-sky-900/20 dark:to-teal-900/20 shadow-lg transform scale-105'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-lg bg-white dark:bg-slate-700'
                }`}
              >
                <div className="flex-1">
                  <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white group-hover:text-cyan-600 transition-colors">
                    {template.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="inline-block px-2 py-0.5 bg-gray-100 dark:bg-slate-600 rounded text-xs dark:text-gray-200">
                      {template.platform.charAt(0).toUpperCase() + template.platform.slice(1).replace(/-/g, ' ')}
                    </span>
                    {template.modelVersion && (
                      <span className="inline-block px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded text-xs">
                        {template.modelVersion}
                      </span>
                    )}
                    {template.costPerUse !== undefined && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                        <DollarSign className="w-3 h-3" />
                        {calculateCost(template)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedTemplate && (
          <div className="space-y-6 fade-in">
            <div className="border-t pt-6 customize-section">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-500 pulse" />
                Customize Your Prompt
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {selectedTemplate.variables.map(variable => (
                  <div key={variable} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 capitalize group-hover:text-blue-600 transition-colors">
                        {variable.replace(/_/g, ' ')}
                      </label>
                      {stylePresets[variable as keyof typeof stylePresets] && (
                        <button
                          onClick={() => randomizeVariable(variable)}
                          className="p-1 text-gray-400 hover:text-blue-500 transition-colors tooltip"
                          data-tooltip="Random value"
                        >
                          <Shuffle className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={variables[variable] || ''}
                      onChange={(e) => handleVariableChange(variable, e.target.value)}
                      placeholder={`Enter ${variable.replace(/_/g, ' ')}`}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                    />
                    {stylePresets[variable as keyof typeof stylePresets] && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {stylePresets[variable as keyof typeof stylePresets].slice(0, 6).map(preset => (
                          <button
                            key={preset}
                            onClick={() => handleVariableChange(variable, preset)}
                            className="text-xs px-2 py-1 bg-gray-50 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:text-blue-700 rounded-full transition-all hover-scale border border-gray-200 hover:border-blue-300"
                          >
                            {preset}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <span className="text-2xl">✨</span> Generated Prompt
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={enhanceWithAI}
                    disabled={isEnhancing || !generatedPrompt}
                    className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg transition-all flex items-center gap-2 text-sm sm:text-base ${
                      isEnhancing
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-cyan-500 to-sky-500 text-white hover:from-cyan-600 hover:to-sky-600 shadow-sm'
                    }`}
                    title="Enhance prompt with AI"
                  >
                    <Bot className="w-4 h-4" />
                    {isEnhancing ? 'Enhancing...' : 'Enhance with AI'}
                  </button>
                  <button
                    onClick={copyToClipboard}
                    className={`p-2.5 rounded-lg transition-all hover-scale tooltip ${
                      copied 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                    data-tooltip="Copy to clipboard"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={saveToHistory}
                    disabled={savingToCloud}
                    className={`p-2.5 rounded-lg transition-all hover-scale tooltip flex items-center gap-1 ${
                      saved 
                        ? 'bg-green-100 text-green-600' 
                        : savingToCloud
                        ? 'bg-blue-100 text-blue-600'
                        : subscriptionService.canSavePrompts(profile)
                        ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        : 'bg-amber-100 hover:bg-amber-200 text-amber-700'
                    }`}
                    data-tooltip={subscriptionService.canSavePrompts(profile) ? "Save to cloud" : "Upgrade to Pro to save"}
                  >
                    {savingToCloud ? (
                      <>
                        <Cloud className="w-4 h-4 animate-pulse" />
                      </>
                    ) : saved ? (
                      <Save className="w-4 h-4" />
                    ) : isAuthenticated ? (
                      <Cloud className="w-4 h-4" />
                    ) : (
                      <CloudOff className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={downloadPrompt}
                    className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all hover-scale tooltip"
                    data-tooltip="Download as text"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-sky-400 rounded-xl blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
                <div className="relative p-3 sm:p-4 bg-gradient-to-br from-gray-50 via-cyan-50 to-sky-50 dark:from-slate-800 dark:via-slate-700 dark:to-slate-800 rounded-xl border border-gray-200 dark:border-gray-600">
                  <p className="text-gray-800 dark:text-gray-200 font-mono text-xs sm:text-sm whitespace-pre-wrap">
                    {generatedPrompt || 'Your AI prompt will appear here...'}
                  </p>
                </div>
              </div>
              
              {selectedTemplate && (
                <div className="mt-3 p-2 sm:p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
                  <p className="text-xs text-cyan-700 flex items-start gap-1">
                    <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>
                      {selectedTemplate.platform === 'midjourney' && 'This prompt is optimized for Midjourney v7. Copy and paste directly into Discord.'}
                      {selectedTemplate.platform === 'midjourney-video' && 'This prompt is optimized for Midjourney Video. Use the --video flag in Discord.'}
                      {selectedTemplate.platform === 'kling-ai' && 'This prompt is optimized for Kling AI video generation.'}
                      {selectedTemplate.platform === 'google-imagefx' && 'This prompt is optimized for Google ImageFX.'}
                      {selectedTemplate.platform === 'dalle3' && 'This prompt is optimized for DALL-E 3.'}
                      {selectedTemplate.platform === 'stable-diffusion-xl' && 'This prompt is optimized for Stable Diffusion XL.'}
                      {selectedTemplate.platform === 'veo' && 'This prompt is optimized for Google Veo video generation.'}
                      {selectedTemplate.platform === 'claude' && 'This prompt is optimized for Claude AI.'}
                      {selectedTemplate.platform === 'gpt4' && 'This prompt is optimized for GPT-4.'}
                      {selectedTemplate.platform === 'gpt4-vision' && 'This prompt is optimized for GPT-4 Vision.'}
                      {selectedTemplate.platform === 'gemini' && 'This prompt is optimized for Google Gemini.'}
                      {selectedTemplate.platform === 'llama' && 'This prompt is optimized for LLaMA.'}
                      {selectedTemplate.platform === 'mistral' && 'This prompt is optimized for Mistral AI.'}
                      {selectedTemplate.platform === 'universal' && 'This is a universal prompt that works across multiple platforms.'}
                      {!['midjourney', 'midjourney-video', 'kling-ai', 'google-imagefx', 'dalle3', 'stable-diffusion-xl', 'veo', 'claude', 'gpt4', 'gpt4-vision', 'gemini', 'llama', 'mistral', 'universal'].includes(selectedTemplate.platform) && 
                        `This prompt is optimized for ${selectedTemplate.platform.charAt(0).toUpperCase() + selectedTemplate.platform.slice(1).replace(/-/g, ' ')}.`}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* AI Assistant */}
      <AIAssistant 
        currentPrompt={generatedPrompt}
        platform={selectedTemplate?.platform || 'universal'}
        onPromptUpdate={handleAIPromptUpdate}
      />
      
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="Save prompts to cloud"
      />
    </div>
  );
};

export default PromptBuilderEnhanced;